import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Bookings from './pages/bookings/Bookings';
import Settings from './pages/Settings';
import ChooseBooking from './pages/bookings/ChooseBooking';
import UserProfile from './pages/UserProfile';
import Users from './pages/Users';
import Racers from './pages/Racers';
import PrivateEvents from './pages/PrivateEvents';
import './App.css';
import GokartBookings from './pages/bookings/GokartBookings';
import SimulatorBookings from './pages/bookings/SimulatorBookings';
import VRBookings from './pages/bookings/VRBookings';
import PlayhouseBookings from './pages/bookings/PlayhouseBookings';
import Registration from './pages/Registration';
import ResetPassword from './pages/ResetPassword';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-600">Betöltés...</p>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Admin route component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-600">Betöltés...</p>
      </div>
    );
  }

  if (!currentUser || !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/registration" element={<Registration />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute>
            <Bookings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <AdminRoute>
            <Settings />
          </AdminRoute>
        }
      />
      <Route
        path="/users"
        element={
          <AdminRoute>
            <Users />
          </AdminRoute>
        }
      />
      <Route
        path="/racers"
        element={
          <AdminRoute>
            <Racers />
          </AdminRoute>
        }
      />
      <Route
        path="/private-events"
        element={
          <AdminRoute>
            <PrivateEvents />
          </AdminRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        }
      />

      <Route path="/public-booking">
        <Route index element={<ChooseBooking />} />
        <Route path="gokart" element={<GokartBookings />} />
        <Route path="vr" element={<VRBookings />} />
        <Route path="simulator" element={<SimulatorBookings />} />
        <Route path="playhouse" element={<PlayhouseBookings />} />
      </Route>
      <Route path="/" element={<Navigate to="/public-booking" />} />
      <Route path="*" element={<Navigate to="/public-booking" />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App; 